// client/src/coms/organisation/channelLayout.js
// The shared channel ordering on the client: text channels, voice channels, categories and dividers in one
// list (server: routes/comms/organisation/channelLayout.js). Pure tree operations used by the sidebar's
// drag-and-drop and keyboard moves and by Server Settings → Channels & Organisation, plus the API calls.
// A tree is [{ type: 'text' | 'voice' | 'divider' | 'category', id, name?, children? }]; categories hold
// channels and dividers, never other categories.
import { apiDelete, apiGetJson, apiPatchJson, apiPostJson, apiPut } from './orgApi';

export const itemKey = (it) => `${it.type}:${it.id}`;

const base = (serverKey) => `/servers/${encodeURIComponent(serverKey)}`;

export function fetchLayout(serverKey) {
  return apiGetJson(`${base(serverKey)}/channel-layout`);
}

// Only types and ids go to the server; it validates and keeps anything this client could not see in place.
export function toRefs(tree) {
  return (tree || []).map((it) => (it.type === 'category' ? { type: 'category', id: it.id, children: (it.children || []).map((c) => ({ type: c.type, id: c.id })) } : { type: it.type, id: it.id }));
}

export function saveLayout(serverKey, tree) {
  return apiPut(`${base(serverKey)}/channel-layout`, { items: toRefs(tree) });
}

export function resetLayout(serverKey) {
  return apiPostJson(`${base(serverKey)}/channel-layout/reset`, { confirm: true });
}

export const addDivider = (serverKey, name, categoryId) => apiPostJson(`${base(serverKey)}/dividers`, { name: name || '', category_id: categoryId || null });
export const renameDivider = (serverKey, id, name) => apiPatchJson(`${base(serverKey)}/dividers/${id}`, { name: name || '' });
export const deleteDivider = (serverKey, id) => apiDelete(`${base(serverKey)}/dividers/${id}`);
export const addCategory = (serverKey, name) => apiPostJson(`${base(serverKey)}/categories`, { name });
export const renameCategory = (serverKey, id, name) => apiPatchJson(`${base(serverKey)}/categories/${id}`, { name });
export const deleteCategory = (serverKey, id) => apiDelete(`${base(serverKey)}/categories/${id}`);
export const addChannel = (serverKey, kind, name, categoryId) => apiPostJson(`${base(serverKey)}/channels`, { kind, name, category_id: categoryId || null });

function clone(tree) {
  return (tree || []).map((it) => (it.type === 'category' ? { ...it, children: (it.children || []).slice() } : it));
}

// { list, index, parent } of an item, or null.
export function locate(tree, key) {
  for (let i = 0; i < tree.length; i += 1) {
    if (itemKey(tree[i]) === key) return { list: tree, index: i, parent: null };
    const kids = tree[i].children;
    if (kids) {
      const j = kids.findIndex((c) => itemKey(c) === key);
      if (j >= 0) return { list: kids, index: j, parent: tree[i] };
    }
  }
  return null;
}

// Moves an item. target: { before: key } | { into: categoryKey } (end of that category) | { end: true }
// (end of the top level). Returns a new tree, or null when nothing would change or the move is not allowed.
export function moveItem(tree, key, target) {
  if (!target || key === target.before) return null;
  const next = clone(tree);
  const from = locate(next, key);
  if (!from) return null;
  const isCategory = from.list[from.index].type === 'category';
  const [item] = from.list.splice(from.index, 1);
  if (target.end) next.push(item);
  else if (target.into) {
    const at = locate(next, target.into);
    if (!at || at.list[at.index].type !== 'category') return null;
    if (isCategory) next.splice(next.indexOf(at.list[at.index]) + 1, 0, item);
    else at.list[at.index].children.push(item);
  } else {
    const at = locate(next, target.before);
    if (!at) return null;
    // Categories stay at the top level: dropped on a channel inside a category, they go before that category.
    if (isCategory && at.parent) next.splice(next.indexOf(at.parent), 0, item);
    else at.list.splice(at.index, 0, item);
  }
  return JSON.stringify(toRefs(next)) === JSON.stringify(toRefs(tree)) ? null : next;
}

// One step up (-1) or down (+1) in the order people see, for keyboard and touch "Move up / Move down".
// A channel or divider at the edge of a category steps out of it; next to an (expanded) category it steps
// into it. Categories swap with their top-level neighbours. collapsed: Set of category keys not to enter.
export function stepItem(tree, key, delta, collapsed = new Set()) {
  const next = clone(tree);
  const at = locate(next, key);
  if (!at) return null;
  const item = at.list[at.index];
  if (at.parent) {
    const j = at.index + delta;
    if (j >= 0 && j < at.list.length) {
      at.list.splice(at.index, 1);
      at.list.splice(j, 0, item);
    } else {
      at.list.splice(at.index, 1);
      const p = next.indexOf(at.parent);
      next.splice(delta < 0 ? p : p + 1, 0, item);
    }
    return next;
  }
  const j = at.index + delta;
  if (j < 0 || j >= next.length) return null;
  const neighbour = next[j];
  if (item.type !== 'category' && neighbour.type === 'category' && !collapsed.has(itemKey(neighbour))) {
    next.splice(at.index, 1);
    if (delta < 0) neighbour.children.push(item);
    else neighbour.children.unshift(item);
    return next;
  }
  next.splice(at.index, 1);
  next.splice(j, 0, item);
  return next;
}

// Moves an item to the end of a category, or to the end of the top level (categoryKey empty).
export function moveToCategory(tree, key, categoryKey) {
  return moveItem(tree, key, categoryKey ? { into: categoryKey } : { end: true });
}

export function categoriesOf(tree) {
  return (tree || []).filter((it) => it.type === 'category');
}
